// What gTables offers Giraffe's MCP relay, apart from the socket (mcpBridge.ts)
// so it can be tested. Same contract as the schematic and chat-code apps:
//
//  - Results are `{ status: "ok", data }`, or `{ status: "blocked", error }` when
//    the tool did not run. A tool that ran and refused is `ok` with data that
//    says so, so a failure and an empty success never share a shape.
//  - Arguments are checked against the tool's schema here as well as at the
//    relay, since `window.__giraffeMcp` has no relay in front of it.
//  - One call at a time: a second is refused before it starts, so retrying is safe.
//  - Writes only touch ordinary sheets. System sheets are rebuilt from the
//    project, and the editable ones push straight to Giraffe.

import type { CellValue, Sheet, SourceKind, Workbook } from '@/model/types';
import type { GiraffeData } from '@/data/types';
import { cellRawText, getCell, isSystemSheet } from '@/model/workbook';
import { parseCellA1, parseRange, rangeToA1, splitSheetPrefix } from '@/model/address';

/** The relay keeps only tools named `${APP}__…`; anything else is dropped in silence. */
export const APP = 'gtables';
/** Most cells one read returns, so an agent cannot pull a 50,000-row Areas sheet into its context by accident. */
export const MAX_READ_CELLS = 2000;
const SOURCES: readonly SourceKind[] = ['areas', 'bakedSections', 'rawSections', 'usages'];

/** What the tools need from the app — the store implements it. */
export interface McpHost {
  wb: Workbook;
  activeSheetId(): string;
  valueAt(sheet: Sheet, row: number, col: number): CellValue | undefined;
  evaluate(formula: string, sheetName: string): CellValue;
  data(): GiraffeData;
  writeCells(sheetId: string, row: number, col: number, grid: string[][]): number;
  addSheet(name: string): Sheet;
}

type Prop = { type: string; description: string };
type Schema = { type: 'object'; properties: Record<string, Prop>; required?: string[]; additionalProperties: false };
interface ToolDef {
  name: string;
  description: string;
  inputSchema: Schema;
  write: boolean;
}

const RANGE_HELP = 'A1 range with an optional sheet prefix, e.g. "Sheet1!A1:D20" or "\'My Sheet\'!B2". Without a prefix, the sheet open on screen.';

const DEFS: ToolDef[] = [
  {
    name: 'list_sheets',
    description:
      'The workbook\'s sheets in tab order: name, role (absent for an ordinary sheet; areas/features/usages/blocks are rebuilt from the Giraffe project), used size, and which one is open. Start here.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    write: false,
  },
  {
    name: 'read_range',
    description: `Cell values (computed) and, where a cell holds one, its formula. Capped at ${MAX_READ_CELLS} cells; read big sheets in pieces.`,
    inputSchema: {
      type: 'object',
      properties: { range: { type: 'string', description: RANGE_HELP } },
      required: ['range'],
      additionalProperties: false,
    },
    write: false,
  },
  {
    name: 'evaluate',
    description:
      'Evaluate a formula without writing it anywhere, e.g. =GI.TOTAL("grossArea", "usage=Residential") or =SUM(Sheet1!B2:B9). GI.* functions read the Areas tab\'s rows (one per level of each stack). Use it to check a formula before writing it.',
    inputSchema: {
      type: 'object',
      properties: {
        formula: { type: 'string', description: 'The formula, with or without the leading =.' },
        sheet: { type: 'string', description: 'Sheet whose unprefixed references it resolves against. Defaults to the open sheet.' },
      },
      required: ['formula'],
      additionalProperties: false,
    },
    write: false,
  },
  {
    name: 'list_fields',
    description:
      'The project fields GI.* formulas can name, with each one\'s measure class (plan, total, rate, count…). Source defaults to "areas", what GI.* reads unless a call names another.',
    inputSchema: {
      type: 'object',
      properties: { source: { type: 'string', description: `One of ${SOURCES.join(', ')}. Default areas.` } },
      additionalProperties: false,
    },
    write: false,
  },
  {
    name: 'write_cells',
    description:
      'Write a block of cells starting at one cell of an ORDINARY sheet (system sheets are refused). Each value is raw text exactly as typed: "=…" is a formula, "12%" a percentage, "" clears. One undo step in the app. Returns the computed values written.',
    inputSchema: {
      type: 'object',
      properties: {
        start: { type: 'string', description: 'Top-left cell, e.g. "Sheet1!B2". Without a prefix, the open sheet.' },
        values: { type: 'array', description: 'Rows of cells: an array of arrays of strings, e.g. [["Item","Area"],["Resi","=GI.TOTAL(\\"grossArea\\")"]].' },
      },
      required: ['start', 'values'],
      additionalProperties: false,
    },
    write: true,
  },
  {
    name: 'add_sheet',
    description: 'Add an ordinary sheet (placed before the system tabs). The name is made unique if taken. Does not switch the user to it.',
    inputSchema: {
      type: 'object',
      properties: { name: { type: 'string', description: 'Sheet name.' } },
      required: ['name'],
      additionalProperties: false,
    },
    write: true,
  },
];

/** Our tools as the relay declares them. */
export const RELAY_TOOLS = DEFS.map((t) => ({
  name: `${APP}__${t.name}`,
  description: t.description,
  inputSchema: t.inputSchema,
  destructiveHint: t.write,
  canAwaitUser: false,
  settlesBeforeResolving: true,
}));

export type ToolErrorCode = 'unknown_tool' | 'invalid_arguments' | 'host_rejected';
export type ToolResult =
  | { status: 'ok'; data: unknown }
  | { status: 'blocked'; inFlight: string; instruction: string }
  | { status: 'blocked'; error: { code: ToolErrorCode; message: string } };

const ok = (data: unknown): ToolResult => ({ status: 'ok', data: data === undefined ? null : data });
const failed = (code: ToolErrorCode, message: string): ToolResult => ({ status: 'blocked', error: { code, message } });
/** Ran, and refused: a fact about the request, not a failure to run. */
const refused = (error: string): ToolResult => ok({ applied: false, error });

export type ValidationResult = { ok: true } | { ok: false; path: string; message: string };

/** Arguments against a tool's schema: required, unknown and each property's type. */
export function validateArguments(schema: Schema, args: unknown): ValidationResult {
  if (typeof args !== 'object' || args === null || Array.isArray(args)) return { ok: false, path: 'arguments', message: 'arguments must be a JSON object' };
  const value = args as Record<string, unknown>;
  for (const name of schema.required ?? [])
    if (!Object.hasOwn(value, name)) return { ok: false, path: `arguments.${name}`, message: `required property "${name}" is missing` };
  for (const name of Object.keys(value))
    if (!Object.hasOwn(schema.properties, name))
      return { ok: false, path: `arguments.${name}`, message: `unknown property "${name}" — this tool takes ${Object.keys(schema.properties).join(', ') || 'no arguments'}` };
  for (const [name, p] of Object.entries(schema.properties)) {
    if (!Object.hasOwn(value, name)) continue;
    const v = value[name];
    const actual = v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v;
    if (actual !== p.type) return { ok: false, path: `arguments.${name}`, message: `expected ${p.type}, received ${actual}` };
  }
  return { ok: true };
}

function sheetNamed(wb: Workbook, name: string | undefined, fallbackId: string): Sheet | null {
  if (name === undefined) return wb.sheets.find((s) => s.id === fallbackId) ?? null;
  const want = name.toLowerCase();
  return wb.sheets.find((s) => s.name.toLowerCase() === want) ?? null;
}

const sheetList = (wb: Workbook): string => wb.sheets.map((s) => s.name).join(', ');

/** A cell value as JSON can carry it: errors stay as their text (#REF!, #DIV/0!). */
function plain(v: CellValue | undefined): string | number | boolean | null {
  return v === undefined ? null : v;
}

function run(host: McpHost, bare: string, args: Record<string, unknown>): ToolResult {
  const wb = host.wb;
  switch (bare) {
    case 'list_sheets': {
      const active = host.activeSheetId();
      return ok({
        sheets: wb.sheets.map((s) => ({
          name: s.name,
          ...(s.role ? { role: s.role } : {}),
          rows: s.rows,
          cols: s.cols,
          ...(s.id === active ? { open: true } : {}),
          writable: !isSystemSheet(s),
        })),
      });
    }

    case 'read_range': {
      const range = parseRange(String(args.range));
      if (!range) return refused(`"${String(args.range)}" is not an A1 range.`);
      const sheet = sheetNamed(wb, range.sheet, host.activeSheetId());
      if (!sheet) return refused(`No sheet named "${range.sheet}". Sheets: ${sheetList(wb)}.`);
      const r2 = Math.min(range.r2, Math.max(range.r1, sheet.rows));
      const c2 = Math.min(range.c2, Math.max(range.c1, sheet.cols));
      const cells = (r2 - range.r1 + 1) * (c2 - range.c1 + 1);
      if (cells > MAX_READ_CELLS)
        return refused(`That is ${cells} cells; one read returns at most ${MAX_READ_CELLS}. Read it in smaller ranges.`);
      const values: (string | number | boolean | null)[][] = [];
      const formulas: Record<string, string> = {};
      for (let r = range.r1; r <= r2; r++) {
        const row: (string | number | boolean | null)[] = [];
        for (let c = range.c1; c <= c2; c++) {
          const cell = getCell(sheet, r, c);
          row.push(plain(host.valueAt(sheet, r, c)));
          if (cell?.f !== undefined) formulas[rangeToA1({ r1: r, c1: c, r2: r, c2: c })] = cellRawText(cell);
        }
        values.push(row);
      }
      return ok({ sheet: sheet.name, range: rangeToA1({ r1: range.r1, c1: range.c1, r2, c2 }), values, formulas });
    }

    case 'evaluate': {
      const sheet = sheetNamed(wb, args.sheet as string | undefined, host.activeSheetId());
      if (!sheet) return refused(`No sheet named "${String(args.sheet)}". Sheets: ${sheetList(wb)}.`);
      const formula = String(args.formula).trim().replace(/^=/, '');
      if (formula === '') return refused('The formula is empty.');
      return ok({ formula: `=${formula}`, value: plain(host.evaluate(formula, sheet.name)) });
    }

    case 'list_fields': {
      const source = (args.source as string | undefined) ?? 'areas';
      const kind = SOURCES.find((s) => s.toLowerCase() === source.toLowerCase());
      if (!kind) return refused(`Unknown source "${source}". Expected one of ${SOURCES.join(', ')}.`);
      const data = host.data();
      return ok({
        source: kind,
        rows: data.rows(kind).length,
        fields: data.fields(kind).map((f) => ({ name: f, class: data.classOf(kind, f) })),
      });
    }

    case 'write_cells': {
      const start = String(args.start);
      const prefix = splitSheetPrefix(start.trim());
      const at = parseCellA1(prefix ? prefix.rest : start);
      if (!at) return refused(`"${start}" is not a single cell like "Sheet1!B2".`);
      const sheet = sheetNamed(wb, prefix?.sheet, host.activeSheetId());
      if (!sheet) return refused(`No sheet named "${prefix?.sheet}". Sheets: ${sheetList(wb)}.`);
      if (isSystemSheet(sheet))
        return refused(`"${sheet.name}" is a system sheet, rebuilt from the Giraffe project. Write to an ordinary sheet, or add one with ${APP}__add_sheet.`);
      const rows = args.values as unknown[];
      if (!rows.every((r) => Array.isArray(r) && r.every((v) => ['string', 'number', 'boolean'].includes(typeof v) || v === null)))
        return refused('values must be an array of rows, each an array of strings (numbers and booleans are accepted too).');
      const grid = (rows as unknown[][]).map((r) => r.map((v) => (v === null ? '' : String(v))));
      const width = Math.max(0, ...grid.map((r) => r.length));
      if (grid.length * width > MAX_READ_CELLS) return refused(`At most ${MAX_READ_CELLS} cells per write.`);
      const written = host.writeCells(sheet.id, at.row, at.col, grid);
      const r2 = at.row + grid.length - 1;
      const c2 = at.col + width - 1;
      const values = grid.map((r, i) => r.map((_, j) => plain(host.valueAt(sheet, at.row + i, at.col + j))));
      return ok({ applied: true, sheet: sheet.name, range: rangeToA1({ r1: at.row, c1: at.col, r2, c2 }), written, values });
    }

    case 'add_sheet': {
      const name = String(args.name).trim();
      if (name === '') return refused('The name is empty.');
      const sheet = host.addSheet(name);
      return ok({ applied: true, name: sheet.name });
    }
  }
  return failed('unknown_tool', `No tool named "${bare}".`);
}

/** The tools, answered against the live app through `host`. */
export function createDispatcher(host: () => McpHost) {
  const byName = new Map(RELAY_TOOLS.map((t) => [t.name, t]));
  let inFlight: string | null = null;

  const call = (name: string, args: unknown): ToolResult => {
    const tool = byName.get(name) ?? byName.get(`${APP}__${name}`);
    if (!tool) return failed('unknown_tool', `No tool named "${name}". Live here: ${[...byName.keys()].join(', ')}.`);
    const valid = validateArguments(tool.inputSchema, args);
    if (!valid.ok) return failed('invalid_arguments', `${tool.name}: ${valid.path} — ${valid.message}. This call never reached the workbook.`);
    if (inFlight !== null)
      return { status: 'blocked', inFlight, instruction: `A call to "${inFlight}" is still running. This call was refused before it started, so it is safe to retry.` };
    inFlight = tool.name;
    try {
      return run(host(), tool.name.slice(APP.length + 2), args as Record<string, unknown>);
    } catch (e) {
      return failed('host_rejected', `${tool.name} threw: ${e instanceof Error ? e.message : String(e)}. Read the sheet again to see whether it took effect.`);
    } finally {
      inFlight = null;
    }
  };

  return { call, list: () => [...byName.keys()], tools: RELAY_TOOLS };
}
